import React, { useMemo } from 'react';
import { IOrderFlexibleColumns, TableCellReducer } from '../types';
import HeaderCell from '../HeaderCell';
import Table from '../index';
import { CheckBoxStates } from '../constants';
import { TypeOrder } from '../interface';
import { ClassNameMap } from '@mui/material';
import { OrderType } from '../../../../utils/gridSorting';

interface FlexibleColumnsTableProps {
  order: TypeOrder;
  orderBy: string;
  onSort: (order: OrderType, orderBy: string) => void;
  onSortColumns: (dragId: string, destinationId: string) => void;
  data: any;
  columnsList: IOrderFlexibleColumns[];
  classes?: ClassNameMap<any>;
  cellReducers: TableCellReducer[];
  colgroup?: JSX.Element;
  onSelectAllRows: () => void;
  selectAllValue?: CheckBoxStates | undefined;
  disableSelectAll?: boolean;
  stickyHeader?: boolean;
}

function FlexibleColumnsTable({
  order,
  orderBy,
  onSort,
  onSortColumns,
  data,
  columnsList,
  classes,
  cellReducers,
  colgroup,
  onSelectAllRows,
  selectAllValue,
  disableSelectAll,
  stickyHeader,
}: FlexibleColumnsTableProps) {
  const columns = useMemo(() => {
    return columnsList
      .filter((f) => f !== undefined)
      .map(({ id, align }) => ({
        id,
        align,
        cellReducer: cellReducers.find((f) => f.id === id)?.cellReducer,
      }));
  }, [classes, columnsList]);

  const headCells = useMemo(
    () =>
      columnsList
        .filter((f) => f !== undefined)
        .map(
          (
            { label, id, sortable, sortingKey, notDraggable, align },
            index,
          ) => ({
            id: id,
            cellReducer: () => (
              <HeaderCell
                index={index}
                id={id}
                onSortColumns={onSortColumns}
                label={label}
                order={order}
                orderBy={orderBy}
                onSort={onSort}
                sortable={sortable}
                sortingKey={sortingKey}
                draggable={!notDraggable}
                onSelectAllRows={onSelectAllRows}
                selectAllValue={selectAllValue}
                disableSelectAll={disableSelectAll}
                alignHeaderLabel={align}
              />
            ),
          }),
        ),
    [columnsList, onSortColumns, order, orderBy, onSort, selectAllValue],
  );

  return (
    <>
      {columnsList.filter((f) => f && f.label !== '').length > 0 && (
        <Table
          headCells={headCells}
          columns={columns}
          rowsData={data}
          styles={classes}
          colgroup={colgroup}
          stickyHeader={stickyHeader}
        />
      )}
    </>
  );
}

export default FlexibleColumnsTable;
